import { Controller } from "@hotwired/stimulus";

// Copies a generated QR code to the clipboard, either as a PNG image (to paste
// into a document or slide) or as its encoded content.
//
// The async Clipboard API only exists in a secure context. Production is HTTPS,
// but local development runs on a plain-HTTP hostname (localhost is exempt,
// named hosts are not), so the API is unavailable there. Text falls back to
// execCommand; images have no equivalent fallback and offer Download instead.
export default class extends Controller {
  static targets = ["status"];
  static values = { content: String, imageUrl: String };

  copyContent() {
    if (!this.contentValue) return;

    if (!navigator.clipboard?.writeText) {
      this.#copyTextLegacy(this.contentValue);
      return;
    }

    navigator.clipboard
      .writeText(this.contentValue)
      .then(() => this.#report("Content copied to clipboard."))
      .catch(() => this.#copyTextLegacy(this.contentValue));
  }

  copyImage() {
    if (!this.imageUrlValue) return;

    if (typeof ClipboardItem === "undefined" || !navigator.clipboard?.write) {
      this.#report(
        window.isSecureContext
          ? "This browser cannot copy images. Use Download PNG instead."
          : "Copying images requires a secure (https) connection. Use Download PNG instead.",
        true
      );
      return;
    }

    // Safari requires ClipboardItem to receive the promise itself rather than an
    // already-awaited blob, so the write stays inside the user gesture.
    const blob = fetch(this.imageUrlValue).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`);
      return response.blob();
    });

    navigator.clipboard
      .write([new ClipboardItem({ "image/png": blob })])
      .then(() => this.#report("Image copied to clipboard."))
      .catch(() => this.#report("Could not copy the image. Use Download PNG instead.", true));
  }

  // Pre-Clipboard-API copy, still available on insecure origins.
  #copyTextLegacy(text) {
    const field = document.createElement("textarea");
    field.value = text;
    field.setAttribute("readonly", "");
    field.style.position = "fixed";
    field.style.top = "-1000px";
    document.body.appendChild(field);
    field.select();

    let copied = false;
    try {
      copied = document.execCommand("copy");
    } catch {
      copied = false;
    }
    document.body.removeChild(field);

    this.#report(copied ? "Content copied to clipboard." : "Could not copy the content.", !copied);
  }

  #report(message, isError = false) {
    if (!this.hasStatusTarget) return;

    this.statusTarget.textContent = message;
    this.statusTarget.classList.toggle("text-danger", isError);
    this.statusTarget.classList.toggle("text-muted", !isError);
  }
}
